import {
  alpha,
  Box,
  CircularProgress,
  Container,
  Grid2,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { HomeBg } from "assets/index";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { useState } from "react";
import { toast } from "react-toastify";
import { ContactUsRequest } from "reduxStore/dataTypes";
import { postContactUs } from "services/apiService";
import { globalStyle } from "themes/global.styles";
import theme, { responsiveFontSize } from "themes/Theme";

const ContactUsPage = () => {
  const [loading, setLoading] = useState(false);
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
    description: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    company: "",
    description: "",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });

    // Clear the error when user starts typing
    setErrors({ ...errors, [name]: "" });
  };

  const validateForm = () => {
    let newErrors = { ...errors };
    let isValid = true;

    if (!formValues.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formValues.description.trim()) {
      newErrors.description = "Description is required";
      isValid = false;
    }

    if (!formValues.company.trim()) {
      newErrors.company = "Company name is required";
      isValid = false;
    }

    if (!formValues.address.trim()) {
      newErrors.address = "Address is required";
      isValid = false;
    }

    if (!formValues.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!formValues.phone.match(/^\d{10}$/)) {
      newErrors.phone = "Phone number must be 10 digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCaptchaChange = (value: string | null) => {
    setCaptchaValue(value);
    console.log("reCAPTCHA value:", value);
  };

  const submitContactForm = async () => {
    if (validateForm()) {
      setLoading(true);
      const emailParams: ContactUsRequest = {
        name: formValues.name,
        email: formValues.email,
        address: formValues.address,
        telephone: formValues.phone,
        description: formValues.description,
      };

      try {
        const response = await postContactUs(emailParams);
        if (response && response.status === 200) {
          toast.success(response?.data?.message);
          setFormValues({
            name: "",
            email: "",
            company: "",
            phone: "",
            address: "",
            description: "",
          });
        }
      } catch (error) {
        console.error("EmailJS Error:", error);
        toast.error("Failed to send email.");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.pageHeroBg}>
        <center>
          <Typography sx={styles.pageTitle}>Contact Us</Typography>
        </center>
      </Box>
      <Container sx={{ py: 4 }}>
        <Typography sx={{ ...globalStyle.sectionHeading, lineHeight: 1.2 }}>
          Write us <br />
          what is in your<span>mind</span>
        </Typography>

        <Grid2 container spacing={2} mt={4}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Name</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="name"
              placeholder="Enter your name"
              sx={styles.inputFieldStyle}
              value={formValues.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Email</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="email"
              placeholder="Enter your email address"
              sx={styles.inputFieldStyle}
              value={formValues.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Phone Number</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="phone"
              placeholder="Enter your phone number"
              sx={styles.inputFieldStyle}
              value={formValues.phone}
              onChange={handleInputChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Company</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="company"
              placeholder="Enter your company name"
              sx={styles.inputFieldStyle}
              value={formValues.company}
              onChange={handleInputChange}
              error={!!errors.company}
              helperText={errors.company}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Address</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="address"
              placeholder="Enter address"
              multiline
              rows={4}
              sx={styles.inputFieldStyle}
              value={formValues.address}
              onChange={handleInputChange}
              error={!!errors.address}
              helperText={errors.address}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Typography sx={styles.inputFieldLabel}>Description</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              name="description"
              placeholder="Enter description"
              multiline
              rows={4}
              sx={styles.inputFieldStyle}
              value={formValues.description}
              onChange={handleInputChange}
              error={!!errors.description}
              helperText={errors.description}
            />
          </Grid2>
        </Grid2>
        <Stack direction={"row"} justifyContent={"flex-end"} mt={4}>
          {loading ? (
            <CircularProgress />
          ) : (
            <PrimaryButton title="Submit" onClick={submitContactForm} />
          )}
        </Stack>
      </Container>
    </Box>
  );
};
export default ContactUsPage;
const styles = {
  container: {
    minHeight: "100vh",
  },
  pageHeroBg: {
    backgroundImage: `url(${HomeBg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    borderBottomLeftRadius: "50px",
    borderBottomRightRadius: "50px",
    width: "100%",
    padding: "1em",
  },
  pageTitle: {
    py: 15,
    fontSize: responsiveFontSize(75),
    fontWeight: 400,
    color: theme.palette.custom.white,
  },
  inputFieldStyle: {
    input: {
      style: {
        backgroundColor: theme.palette.secondary.main,
      },
    },
  },
  inputFieldLabel: {
    fontSize: responsiveFontSize(14),
    color: theme.palette.primary.main,
    fontWeight: 400,
    marginBottom: "0.3em",
  },
};
